import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { measurementLocalDayUtcRange } from '../utils/measurementTime';
import { Button, Empty, Segmented, Space, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';

import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import '../style/Dashboard.css';

import MeasurementSummaryCard from '../components/Dashboard/MeasurementSummaryCard';
import TrendIndicator, { TrendDirection } from '../components/Dashboard/TrendIndicator';
import DashboardExecutiveView from '../components/Dashboard/DashboardExecutiveView';
import DashboardOperationalView from '../components/Dashboard/DashboardOperationalView';
import {
    AreaHealthKpiSummary,
    AreaSummary,
    DashboardCollapseItem,
    DashboardInsight,
    DashboardMetricDisplay,
    DashboardWarning,
    ExecutiveRiskRow,
    HealthTrendPoint,
    LatestMeasurement,
    MeasurementAreaCountRow,
    MetricDefinition,
    RiskLevel,
    RiskTrendWindow,
    ScanTrendSnapshot,
} from '../components/Dashboard/dashboardTypes';
import useAreaHealthStatuses, { AreaHealthStatusRow } from '../hooks/useAreaHealthStatuses';
import { HealthStatus } from '../helpers/reports/areaHealthStatus';
import { resolveBenchmarkRangesForMetric } from '../helpers/metricRules';
import {
    buildBenchmarkMetricCandidateIndex,
    extractBaseMetricKey,
    findBenchmarkMetricCandidates,
    resolveBenchmarkBand,
} from '../components/ReportSections/benchmarkUtils';

const { Title, Text } = Typography;

const DASHBOARD_MODE_STORAGE_KEY = 'oscar.dashboard.mode';
const BENCHMARK_DEBUG_STORAGE_KEY = 'oscar.debug.benchmark';
const BENCHMARK_DEBUG_QUERY_PARAM = 'debugBenchmark';
const EMPTY_DASHBOARD_INSIGHTS: DashboardInsight = {
    warnings: [],
    mostVisited: [],
    lastMeasured: [],
    mostVisitedSummaries: [],
};

type DashboardMode = 'executive' | 'operational';
type HealthScoreBand = 1 | 2 | 3 | 4 | 5;
type OverallHealthTrendSnapshot = {
    direction: TrendDirection;
    delta: number | null;
    currentScore: number | null;
    previousScore: number | null;
};

type BenchmarkTrendSummary = AreaHealthKpiSummary['weekTrend'];
type AreaBenchmarkDebugState = {
    areaId: number;
    areaName: string;
    areaTypeId: number | null;
    seriesMetricKeys: Set<string>;
    resolvedMetricDetails: Set<string>;
    unresolvedMetricDetails: Set<string>;
    missingAveragePoints: Set<string>;
    noBandMatches: Set<string>;
    scoredDays: Set<string>;
};

const RISK_LEVEL_WEIGHT: Record<RiskLevel, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    unknown: 0,
};

const HEALTH_SCORE_LABEL_BY_BAND: Record<HealthScoreBand, string> = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Great',
    5: 'Perfect',
};

const getDefaultDashboardMode = (user: any): DashboardMode => {
    const roleCode = String(user?.currentCompanyRole?.code || user?.role?.code || '').toLowerCase();
    if (user?.isMaster || roleCode === 'admin') {
        return 'executive';
    }
    return 'operational';
};

const resolveRiskLevel = (
    roundedHealthScore: number | null,
    status: HealthStatus,
    warningCount: number
): RiskLevel => {
    if (roundedHealthScore !== null) {
        if (roundedHealthScore <= 1) return 'critical';
        if (roundedHealthScore <= 2) return 'high';
        if (roundedHealthScore <= 3) return 'medium';
        return 'low';
    }

    if (status === 'action-needed') return 'critical';
    if (status === 'attention') return 'high';
    if (status === 'healthy' && warningCount > 0) return 'medium';
    if (status === 'healthy') return 'low';
    return 'unknown';
};

const compareRiskRows = (a: ExecutiveRiskRow, b: ExecutiveRiskRow) => {
    const riskDiff = RISK_LEVEL_WEIGHT[b.riskLevel] - RISK_LEVEL_WEIGHT[a.riskLevel];
    if (riskDiff !== 0) return riskDiff;

    if (a.latestHealthScoreRounded !== null && b.latestHealthScoreRounded !== null) {
        if (a.latestHealthScoreRounded !== b.latestHealthScoreRounded) {
            return a.latestHealthScoreRounded - b.latestHealthScoreRounded;
        }
    } else if (a.latestHealthScoreRounded === null && b.latestHealthScoreRounded !== null) {
        return 1;
    } else if (a.latestHealthScoreRounded !== null && b.latestHealthScoreRounded === null) {
        return -1;
    }

    if (b.warningCount !== a.warningCount) return b.warningCount - a.warningCount;

    return a.areaName.localeCompare(b.areaName);
};

const flattenRiskRows = (rows: ExecutiveRiskRow[]): ExecutiveRiskRow[] => {
    const flat: ExecutiveRiskRow[] = [];
    const traverse = (entry: ExecutiveRiskRow) => {
        flat.push(entry);
        if (entry.children && entry.children.length > 0) {
            entry.children.forEach(traverse);
        }
    };
    rows.forEach(traverse);
    return flat;
};

const normalizeHealthBand = (score: number): HealthScoreBand => {
    const rounded = Math.round(score);
    return Math.max(1, Math.min(5, rounded)) as HealthScoreBand;
};

const calculateBenchmarkTrend = (
    dailySeries: Array<{ day: string; score: number }>,
    days: number
): BenchmarkTrendSummary => {
    const end = dayjs().endOf('day');
    const start = end.subtract(days - 1, 'day').startOf('day');
    const filtered = dailySeries.filter((entry) => dayjs(entry.day).valueOf() >= start.valueOf());

    if (filtered.length < 2) {
        return {
            direction: 'none',
            delta: null,
            startScore: filtered[0]?.score ?? null,
            endScore: filtered[filtered.length - 1]?.score ?? null,
            sampleCount: filtered.length,
        };
    }

    const startScore = filtered[0].score;
    const endScore = filtered[filtered.length - 1].score;
    const delta = endScore - startScore;
    const direction: TrendDirection = delta > 0.1 ? 'up' : delta < -0.1 ? 'down' : 'same';

    return {
        direction,
        delta: Number(delta.toFixed(2)),
        startScore: Number(startScore.toFixed(2)),
        endScore: Number(endScore.toFixed(2)),
        sampleCount: filtered.length,
    };
};

const getRiskTrendDateRange = (window: RiskTrendWindow): [Dayjs, Dayjs] => {
    const end = dayjs().endOf('day');
    const start = end.subtract(window === 'week' ? 6 : 29, 'day').startOf('day');
    return [start, end];
};

const getPreviousRiskTrendDateRange = (
    window: RiskTrendWindow,
    currentStart: Dayjs
): [Dayjs, Dayjs] => {
    const previousEnd = currentStart.subtract(1, 'day').endOf('day');
    const previousStart = previousEnd.subtract(window === 'week' ? 6 : 29, 'day').startOf('day');
    return [previousStart, previousEnd];
};

const isBenchmarkDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') return false;

    const queryValue = new URLSearchParams(window.location.search).get(BENCHMARK_DEBUG_QUERY_PARAM);
    if (queryValue && ['1', 'true', 'yes', 'on'].includes(queryValue.toLowerCase())) {
        return true;
    }

    const storedValue = window.localStorage.getItem(BENCHMARK_DEBUG_STORAGE_KEY);
    return !!storedValue && ['1', 'true', 'yes', 'on'].includes(storedValue.toLowerCase());
};

const createAreaBenchmarkDebugState = (area: AreaSummary): AreaBenchmarkDebugState => ({
    areaId: area.id,
    areaName: area.name,
    areaTypeId: area.areaTypeId ?? null,
    seriesMetricKeys: new Set<string>(),
    resolvedMetricDetails: new Set<string>(),
    unresolvedMetricDetails: new Set<string>(),
    missingAveragePoints: new Set<string>(),
    noBandMatches: new Set<string>(),
    scoredDays: new Set<string>(),
});

const Dashboard: React.FC = () => {
    const { currentUser } = useAuth();
    const companyId =
        currentUser?.currentCompanyId || currentUser?.companyId || currentUser?.associatedCompanies?.[0]?.id;
    const navigate = useNavigate();

    const [dashboardMode, setDashboardMode] = useState<DashboardMode>(() => {
        if (typeof window !== 'undefined') {
            const stored = window.localStorage.getItem(DASHBOARD_MODE_STORAGE_KEY);
            if (stored === 'executive-v2') {
                return 'executive';
            }
            if (stored === 'executive' || stored === 'operational') {
                return stored;
            }
        }
        return getDefaultDashboardMode(currentUser);
    });
    const benchmarkDebugEnabled = useMemo(() => isBenchmarkDebugEnabled(), []);

    useEffect(() => {
        if (!benchmarkDebugEnabled) return;
        console.warn('[Dashboard benchmark debug] enabled', {
            queryParam: BENCHMARK_DEBUG_QUERY_PARAM,
            storageKey: BENCHMARK_DEBUG_STORAGE_KEY,
            companyId: companyId ?? null,
        });
    }, [benchmarkDebugEnabled, companyId]);

    const [insights, setInsights] = useState<DashboardInsight | null>(null);
    const [loadingInsights, setLoadingInsights] = useState<boolean>(true);
    const [templateImageUrl] = useState<string | null>(null);
    const [metricDefinitions, setMetricDefinitions] = useState<MetricDefinition[]>([]);
    const [favoriteArea, setFavoriteArea] = useState<AreaSummary | null>(null);
    const [showAllWarnings, setShowAllWarnings] = useState(false);
    const [favoriteMeasurement, setFavoriteMeasurement] = useState<LatestMeasurement | null>(null);
    const [loadingFavoriteMeasurement, setLoadingFavoriteMeasurement] = useState<boolean>(false);

    const [areaSummaries, setAreaSummaries] = useState<AreaSummary[]>([]);
    const [loadingAreas, setLoadingAreas] = useState<boolean>(false);
    const [riskTrendWindow, setRiskTrendWindow] = useState<RiskTrendWindow>('month');
    const [benchmarkMetrics, setBenchmarkMetrics] = useState<any[]>([]);
    const [loadingBenchmarkMetrics, setLoadingBenchmarkMetrics] = useState<boolean>(false);
    const [areaHealthKpis, setAreaHealthKpis] = useState<Map<number, AreaHealthKpiSummary>>(new Map());
    const [loadingAreaHealthKpis, setLoadingAreaHealthKpis] = useState<boolean>(false);
    const [overallSurfaceHealthTrend, setOverallSurfaceHealthTrend] = useState<OverallHealthTrendSnapshot | null>(
        null
    );
    const [portfolioHealthSeries, setPortfolioHealthSeries] = useState<HealthTrendPoint[]>([]);

    const [scanTrendSnapshot, setScanTrendSnapshot] = useState<ScanTrendSnapshot | null>(null);
    const [loadingScanTrend, setLoadingScanTrend] = useState<boolean>(false);

    const preferredMetricKeys = useMemo(
        () => currentUser?.dashboardSettings?.metrics || [],
        [currentUser?.dashboardSettings?.metrics]
    );
    const favoriteAreaId = currentUser?.dashboardSettings?.favoriteAreaId ?? null;

    const defaultDashboardMode = useMemo(
        () => getDefaultDashboardMode(currentUser),
        [currentUser]
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(DASHBOARD_MODE_STORAGE_KEY, dashboardMode);
    }, [dashboardMode]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = window.localStorage.getItem(DASHBOARD_MODE_STORAGE_KEY);
        if (stored === 'executive-v2') {
            setDashboardMode('executive');
            return;
        }
        if (stored !== 'executive' && stored !== 'operational') {
            setDashboardMode(defaultDashboardMode);
        }
    }, [defaultDashboardMode]);

    useEffect(() => {
        if (!companyId) {
            setInsights(EMPTY_DASHBOARD_INSIGHTS);
            setLoadingInsights(false);
            return undefined;
        }

        const controller = new AbortController();
        setLoadingInsights(true);

        const timer = window.setTimeout(async () => {
            try {
                const response = await axios.get(ENDPOINTS.DASHBOARD_INSIGHTS(companyId), {
                    withCredentials: true,
                    signal: controller.signal,
                });
                const payload = response.data?.data || response.data;
                setInsights(payload);
            } catch (error) {
                if (axios.isCancel(error) || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')) {
                    return;
                }
                if (axios.isAxiosError(error)) {
                    console.warn(
                        `Dashboard insights fetch failed: GET ${error.config?.url || 'unknown endpoint'} (${error.response?.status || 'no response'})`,
                        error.response?.data ?? error.message
                    );
                } else {
                    console.warn('Dashboard insights fetch failed', error);
                }
                setInsights(EMPTY_DASHBOARD_INSIGHTS);
            } finally {
                if (!controller.signal.aborted) {
                    setLoadingInsights(false);
                }
            }
        }, 0);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [companyId]);

    useEffect(() => {
        const loadMetricDefinitions = async () => {
            try {
                const response = await axios.get(`${ENDPOINTS.METRICS}?active=true`, { withCredentials: true });
                const metrics = response.data?.data || [];
                setMetricDefinitions(
                    metrics
                        .filter((metric: any) => typeof metric?.key === 'string')
                        .map((metric: any) => ({
                            key: metric.key,
                            name: metric.name || metric.key,
                            unit: metric.unit || null,
                            sourceKey: metric.sourceKey || null,
                            valueTypeKey: metric.valueTypeKey || null,
                            positiveIsGood: metric.positiveIsGood ?? true,
                        }))
                );
            } catch (error) {
                console.warn('Dashboard metrics fetch failed', error);
            }
        };
        void loadMetricDefinitions();
    }, []);

    useEffect(() => {
        const loadBenchmarkMetrics = async () => {
            if (!companyId) {
                setBenchmarkMetrics([]);
                setLoadingBenchmarkMetrics(false);
                return;
            }

            setLoadingBenchmarkMetrics(true);
            try {
                const response = await axios.get(ENDPOINTS.REPORT_TEMPLATE_WIZARD_META, { withCredentials: true });
                const metaMetrics = response.data?.data?.metrics || [];
                const candidates = (Array.isArray(metaMetrics) ? metaMetrics : []).filter((metric: any) => {
                    if (!metric?.key || metric?.active === false) return false;
                    if (metric?.sourceType !== 'measurement_value') return false;

                    const hasGlobalRanges =
                        !!resolveBenchmarkRangesForMetric(metric) &&
                        Object.keys(resolveBenchmarkRangesForMetric(metric) || {}).length > 0;
                    if (hasGlobalRanges) return true;

                    const perAreaTypeRules = metric?.areaTypeRuleDefaults || {};
                    return Object.values(perAreaTypeRules).some((ruleSet: any) => {
                        const ranges = ruleSet?.benchmarkRange || ruleSet?.benchmarkRanges;
                        return !!ranges && Object.keys(ranges).length > 0;
                    });
                });
                setBenchmarkMetrics(candidates);
            } catch (error) {
                console.warn('Dashboard benchmark metrics fetch failed', error);
                setBenchmarkMetrics([]);
            } finally {
                setLoadingBenchmarkMetrics(false);
            }
        };

        void loadBenchmarkMetrics();
    }, [companyId]);

    const benchmarkMetricCandidateIndex = useMemo(
        () => buildBenchmarkMetricCandidateIndex(Array.isArray(benchmarkMetrics) ? benchmarkMetrics : []),
        [benchmarkMetrics]
    );

    useEffect(() => {
        const loadFavoriteArea = async () => {
            if (!favoriteAreaId) {
                setFavoriteArea(null);
                return;
            }
            try {
                const response = await axios.get(ENDPOINTS.AREA_DETAIL(favoriteAreaId), { withCredentials: true });
                const area = response.data?.data;
                if (area?.id) {
                    setFavoriteArea({ id: area.id, name: area.name || `Area ${area.id}` });
                } else {
                    setFavoriteArea(null);
                }
            } catch (error) {
                console.warn('Dashboard favorite area fetch failed', error);
                setFavoriteArea(null);
            }
        };
        void loadFavoriteArea();
    }, [favoriteAreaId]);

    useEffect(() => {
        const loadFavoriteMeasurement = async () => {
            if (!companyId) {
                setFavoriteMeasurement(null);
                setLoadingFavoriteMeasurement(false);
                return;
            }
            setLoadingFavoriteMeasurement(true);
            try {
                const response = await axios.get(ENDPOINTS.DASHBOARD_FAVORITE_AREA(companyId), {
                    withCredentials: true,
                });
                const payload: LatestMeasurement | null = response.data?.data || null;
                setFavoriteMeasurement(payload);
            } catch (error) {
                console.warn('Failed to load favorite area measurement', error);
                setFavoriteMeasurement(null);
            } finally {
                setLoadingFavoriteMeasurement(false);
            }
        };
        void loadFavoriteMeasurement();
    }, [companyId]);

    useEffect(() => {
        const loadAreas = async () => {
            if (!companyId) {
                setAreaSummaries([]);
                setLoadingAreas(false);
                return;
            }

            const controller = new AbortController();
            let isMounted = true;

            setLoadingAreas(true);
            try {
                const pageSize = 250;
                let offset = 0;
                let total: number | null = null;
                const merged: AreaSummary[] = [];

                while (true) {
                    const response = await axios.get(ENDPOINTS.AREAS_V2, {
                        params: { limit: pageSize, offset },
                        withCredentials: true,
                        signal: controller.signal,
                    });

                    const payload = response.data;
                    const rows = Array.isArray(payload?.data)
                        ? payload.data
                        : Array.isArray(payload)
                        ? payload
                        : [];

                    if (typeof payload?.total === 'number') {
                        total = payload.total;
                    }

                    const normalizedRows = rows
                        .filter((row: any) => Number.isFinite(Number(row?.id)))
                        .map((row: any) => ({
                            id: Number(row.id),
                            name: row.name || `Area ${row.id}`,
                            companyId: Number.isFinite(Number(row?.companyId)) ? Number(row.companyId) : null,
                            parentAreaId: Number.isFinite(Number(row?.parentAreaId))
                                ? Number(row.parentAreaId)
                                : null,
                            areaTypeId: Number.isFinite(Number(row?.areaTypeId))
                                ? Number(row.areaTypeId)
                                : Number.isFinite(Number(row?.areaType?.id))
                                ? Number(row.areaType.id)
                                : null,
                        }))
                        .filter(
                            (row: AreaSummary) =>
                                row.companyId === null ? false : row.companyId === Number(companyId)
                        );

                    merged.push(...normalizedRows);

                    if (rows.length < pageSize) break;
                    offset += pageSize;
                    if (total !== null && merged.length >= total) break;
                    if (offset > 10000) break;
                }

                const deduped = Array.from(
                    merged.reduce((map, area) => {
                        if (!map.has(area.id)) {
                            map.set(area.id, area);
                        }
                        return map;
                    }, new Map<number, AreaSummary>()).values()
                );

                if (isMounted) {
                    setAreaSummaries(deduped);
                }
            } catch (error: any) {
                if (axios.isCancel(error) || error?.name === 'CanceledError') {
                    return;
                }
                console.warn('Dashboard area list fetch failed', error);
                if (isMounted) {
                    setAreaSummaries([]);
                }
            } finally {
                if (isMounted) {
                    setLoadingAreas(false);
                }
            }

            return () => {
                isMounted = false;
                controller.abort();
            };
        };

        let cleanup: (() => void) | void;
        void (async () => {
            cleanup = await loadAreas();
        })();

        return () => {
            if (cleanup) cleanup();
        };
    }, [companyId]);

    useEffect(() => {
        const loadScanTrendSnapshot = async () => {
            if (!companyId) {
                setScanTrendSnapshot(null);
                setLoadingScanTrend(false);
                return;
            }

            setLoadingScanTrend(true);
            try {
                let currentStart: Dayjs;
                let currentEnd: Dayjs;
                let previousStart: Dayjs;
                let previousEnd: Dayjs;

                if (riskTrendWindow === 'week') {
                    currentEnd = dayjs().endOf('day');
                    currentStart = currentEnd.subtract(6, 'day').startOf('day');
                    previousEnd = currentStart.subtract(1, 'day').endOf('day');
                    previousStart = previousEnd.subtract(6, 'day').startOf('day');
                } else {
                    currentStart = dayjs().subtract(1, 'month').startOf('month');
                    currentEnd = dayjs().subtract(1, 'month').endOf('month');
                    previousStart = dayjs().subtract(2, 'month').startOf('month');
                    previousEnd = dayjs().subtract(2, 'month').endOf('month');
                }

                const [currentRes, previousRes] = await Promise.all([
                    axios.get(ENDPOINTS.MEASUREMENTS_AREA_COUNTS, {
                        params: {
                            from_date: currentStart.toISOString(),
                            to_date: currentEnd.toISOString(),
                        },
                        withCredentials: true,
                    }),
                    axios.get(ENDPOINTS.MEASUREMENTS_AREA_COUNTS, {
                        params: {
                            from_date: previousStart.toISOString(),
                            to_date: previousEnd.toISOString(),
                        },
                        withCredentials: true,
                    }),
                ]);

                const parseRows = (payload: any): MeasurementAreaCountRow[] => {
                    const rows = payload?.data?.data || payload?.data || [];
                    if (!Array.isArray(rows)) return [];
                    return rows.map((row: any) => ({
                        areaId: Number.isFinite(Number(row?.areaId)) ? Number(row.areaId) : null,
                        total: Number(row?.total || 0),
                        assignedToPoint: Number(row?.assignedToPoint || 0),
                        inPatternNoPoint: Number(row?.inPatternNoPoint || 0),
                        noPattern: Number(row?.noPattern || 0),
                    }));
                };

                const sumTotals = (rows: MeasurementAreaCountRow[]) =>
                    rows.reduce((sum, row) => sum + (Number.isFinite(row.total) ? row.total : 0), 0);

                const currentCount = sumTotals(parseRows(currentRes));
                const previousCount = sumTotals(parseRows(previousRes));

                let direction: TrendDirection = 'same';
                let deltaPercent: number | null = null;

                if (previousCount === 0) {
                    if (currentCount > 0) {
                        direction = 'up';
                    }
                } else {
                    const delta = (currentCount - previousCount) / previousCount;
                    deltaPercent = delta * 100;
                    if (delta > 0.05) direction = 'up';
                    else if (delta < -0.05) direction = 'down';
                    else direction = 'same';
                }

                setScanTrendSnapshot({
                    currentCount,
                    previousCount,
                    direction,
                    deltaPercent,
                });
            } catch (error) {
                console.warn('Dashboard scan trend fetch failed', error);
                setScanTrendSnapshot(null);
            } finally {
                setLoadingScanTrend(false);
            }
        };

        void loadScanTrendSnapshot();
    }, [companyId, riskTrendWindow]);

    useEffect(() => {
        const loadAreaHealthKpis = async () => {
            if (benchmarkDebugEnabled) {
                console.warn('[Dashboard benchmark debug] loadAreaHealthKpis start', {
                    companyId: companyId ?? null,
                    areaCount: areaSummaries.length,
                    benchmarkMetricCount: benchmarkMetrics.length,
                    riskTrendWindow,
                });
            }
            if (!companyId || areaSummaries.length === 0 || benchmarkMetrics.length === 0) {
                if (benchmarkDebugEnabled) {
                    console.warn('[Dashboard benchmark debug] loadAreaHealthKpis skipped', {
                        missingCompanyId: !companyId,
                        missingAreas: areaSummaries.length === 0,
                        missingBenchmarkMetrics: benchmarkMetrics.length === 0,
                    });
                }
                setAreaHealthKpis(new Map());
                setOverallSurfaceHealthTrend(null);
                setPortfolioHealthSeries([]);
                setLoadingAreaHealthKpis(false);
                return;
            }

            const controller = new AbortController();
            setLoadingAreaHealthKpis(true);

            try {
                const metricKeys = Array.from(
                    new Set(
                        benchmarkMetrics
                            .map((metric: any) => (metric?.key ? String(metric.key) : ''))
                            .filter(Boolean)
                    )
                );

                if (metricKeys.length === 0) {
                    setAreaHealthKpis(new Map());
                    setOverallSurfaceHealthTrend(null);
                    setPortfolioHealthSeries([]);
                    setLoadingAreaHealthKpis(false);
                    return;
                }

                const [currentStart, currentEnd] = getRiskTrendDateRange(riskTrendWindow);
                const [previousStart] = getPreviousRiskTrendDateRange(riskTrendWindow, currentStart);

                const response = await axios.post(
                    ENDPOINTS.REPORT_SECTION_LINE,
                    {
                        areaIds: areaSummaries.map((area) => area.id),
                        metrics: metricKeys,
                        resolution: 'day',
                        dateRange: {
                            from: previousStart.toISOString(),
                            to: currentEnd.toISOString(),
                        },
                    },
                    {
                        withCredentials: true,
                        signal: controller.signal,
                    }
                );

                const seriesPayload = Array.isArray(response.data?.data) ? response.data.data : [];
                const areaById = new Map(areaSummaries.map((area) => [area.id, area]));
                const metricByKey = benchmarkMetrics.reduce((map, metric: any) => {
                    const key = metric?.key ? String(metric.key).toLowerCase() : '';
                    if (key) {
                        map.set(key, metric);
                    }
                    return map;
                }, new Map<string, any>());
                const debugStateByArea = benchmarkDebugEnabled ? new Map<number, AreaBenchmarkDebugState>() : null;
                const getDebugState = (area: AreaSummary) => {
                    if (!debugStateByArea) return null;
                    let state = debugStateByArea.get(area.id);
                    if (!state) {
                        state = createAreaBenchmarkDebugState(area);
                        debugStateByArea.set(area.id, state);
                    }
                    return state;
                };

                const resolveMetricForBenchmarkScore = (rawMetricKey: string, area: AreaSummary) => {
                    const debugState = getDebugState(area);
                    debugState?.seriesMetricKeys.add(rawMetricKey);
                    const directMetric = metricByKey.get(rawMetricKey);
                    const directRanges = directMetric
                        ? resolveBenchmarkRangesForMetric(directMetric, area)
                        : undefined;
                    if (directMetric && directRanges && Object.keys(directRanges).length > 0) {
                        debugState?.resolvedMetricDetails.add(
                            `${rawMetricKey} -> ${String(directMetric.key || rawMetricKey)} (sourceKey=${
                                directMetric?.sourceKey || 'n/a'
                            })`
                        );
                        return {
                            metric: directMetric,
                            ranges: directRanges,
                            metricLookupKey: String(directMetric.key || rawMetricKey).toLowerCase(),
                        };
                    }

                    const candidates = findBenchmarkMetricCandidates(benchmarkMetricCandidateIndex, [
                        rawMetricKey,
                        directMetric?.key,
                        directMetric?.sourceKey,
                        directMetric?.name,
                        extractBaseMetricKey(rawMetricKey),
                        extractBaseMetricKey(directMetric?.key),
                    ]);

                    for (const candidate of candidates) {
                        const ranges = resolveBenchmarkRangesForMetric(candidate, area);
                        if (ranges && Object.keys(ranges).length > 0) {
                            debugState?.resolvedMetricDetails.add(
                                `${rawMetricKey} -> ${String(candidate?.key || rawMetricKey)} (sourceKey=${
                                    candidate?.sourceKey || 'n/a'
                                })`
                            );
                            return {
                                metric: candidate,
                                ranges,
                                metricLookupKey: String(candidate?.key || rawMetricKey).toLowerCase(),
                            };
                        }
                    }

                    debugState?.unresolvedMetricDetails.add(
                        `${rawMetricKey} -> no benchmark ranges; candidates=[${candidates
                            .map((candidate) => `${candidate?.key || 'n/a'}|${candidate?.sourceKey || 'n/a'}`)
                            .join(', ')}]`
                    );
                    return null;
                };

                const dayScoresByArea = new Map<number, Map<string, number[]>>();
                const dayMetricScoresByArea = new Map<
                    number,
                    Map<string, Map<string, { metricName: string; scores: number[] }>>
                >();

                seriesPayload.forEach((metricSeries: any) => {
                    const metricKey = metricSeries?.metricKey ? String(metricSeries.metricKey).toLowerCase() : '';
                    if (!metricKey) return;

                    const points = Array.isArray(metricSeries?.points) ? metricSeries.points : [];
                    points.forEach((point: any) => {
                        const areaId = Number(point?.areaId);
                        if (!Number.isFinite(areaId)) return;

                        const area = areaById.get(areaId);
                        if (!area) return;
                        const debugState = getDebugState(area);

                        const resolvedMetric = resolveMetricForBenchmarkScore(metricKey, area);
                        if (!resolvedMetric) return;

                        const rawAverage = point?.average ?? point?.avg;
                        const average = Number(rawAverage);
                        if (!Number.isFinite(average)) {
                            debugState?.missingAveragePoints.add(
                                `${metricKey} @ ${String(point?.x || 'n/a')} -> rawAverage=${String(rawAverage)}`
                            );
                            return;
                        }

                        const match = resolveBenchmarkBand(average, resolvedMetric.ranges);
                        if (!match) {
                            debugState?.noBandMatches.add(
                                `${resolvedMetric.metricLookupKey} @ ${String(point?.x || 'n/a')} -> average=${average}`
                            );
                            return;
                        }

                        const dayValue = dayjs(point?.x);
                        if (!dayValue.isValid()) return;
                        const dayKey = dayValue.startOf('day').format('YYYY-MM-DD');
                        debugState?.scoredDays.add(dayKey);

                        if (!dayScoresByArea.has(areaId)) {
                            dayScoresByArea.set(areaId, new Map());
                        }
                        const byDay = dayScoresByArea.get(areaId)!;
                        const existingScores = byDay.get(dayKey) || [];
                        existingScores.push(match.score);
                        byDay.set(dayKey, existingScores);

                        if (!dayMetricScoresByArea.has(areaId)) {
                            dayMetricScoresByArea.set(areaId, new Map());
                        }
                        const byMetricDay = dayMetricScoresByArea.get(areaId)!;
                        if (!byMetricDay.has(dayKey)) {
                            byMetricDay.set(dayKey, new Map());
                        }
                        const metricsForDay = byMetricDay.get(dayKey)!;
                        const metricEntry = metricsForDay.get(resolvedMetric.metricLookupKey) || {
                            metricName: resolvedMetric.metric?.name || resolvedMetric.metricLookupKey,
                            scores: [] as number[],
                        };
                        metricEntry.scores.push(match.score);
                        metricsForDay.set(resolvedMetric.metricLookupKey, metricEntry);
                    });
                });

                const nextAreaHealthKpis = new Map<number, AreaHealthKpiSummary>();
                const currentPeriodAreaScores: number[] = [];
                const previousPeriodAreaScores: number[] = [];
                const overallCurrentSeriesByDay = new Map<string, number[]>();

                areaSummaries.forEach((area) => {
                    const byDay = dayScoresByArea.get(area.id) || new Map<string, number[]>();
                    const dailySeries = Array.from(byDay.entries())
                        .map(([day, scores]) => ({
                            day,
                            score: scores.reduce((sum, value) => sum + value, 0) / scores.length,
                            metricCount: scores.length,
                        }))
                        .sort((a, b) => dayjs(a.day).valueOf() - dayjs(b.day).valueOf());

                    const currentWindowSeries = dailySeries.filter((entry) => {
                        const day = dayjs(entry.day);
                        return !day.isBefore(currentStart) && !day.isAfter(currentEnd);
                    });
                    const previousWindowSeries = dailySeries.filter((entry) => {
                        const day = dayjs(entry.day);
                        return !day.isBefore(previousStart) && day.isBefore(currentStart);
                    });

                    const latest = currentWindowSeries[currentWindowSeries.length - 1] || null;
                    const previousLatest = previousWindowSeries[previousWindowSeries.length - 1] || null;
                    const latestScoreRounded =
                        latest && Number.isFinite(latest.score) ? normalizeHealthBand(latest.score) : null;
                    const latestLabel =
                        latestScoreRounded !== null ? HEALTH_SCORE_LABEL_BY_BAND[latestScoreRounded] : null;

                    currentWindowSeries.forEach((entry) => {
                        const existing = overallCurrentSeriesByDay.get(entry.day) || [];
                        existing.push(entry.score);
                        overallCurrentSeriesByDay.set(entry.day, existing);
                    });

                    const latestMetricScores = latest
                        ? dayMetricScoresByArea.get(area.id)?.get(latest.day) || null
                        : null;
                    let worstMetricKey: string | null = null;
                    let worstMetricName: string | null = null;
                    let worstMetricScore: number | null = null;
                    let worstMetricLabel: string | null = null;

                    if (latestMetricScores && latestMetricScores.size > 0) {
                        latestMetricScores.forEach((entry, metricKey) => {
                            if (!entry.scores || entry.scores.length === 0) return;
                            const metricScore =
                                entry.scores.reduce((sum, value) => sum + value, 0) / entry.scores.length;
                            const metricName = entry.metricName || metricKey;

                            const shouldReplace =
                                worstMetricScore === null ||
                                metricScore < worstMetricScore - 0.0001 ||
                                (Math.abs(metricScore - worstMetricScore) <= 0.0001 &&
                                    metricName.localeCompare(worstMetricName || '') < 0);

                            if (shouldReplace) {
                                worstMetricKey = metricKey;
                                worstMetricName = metricName;
                                worstMetricScore = Number(metricScore.toFixed(2));
                                worstMetricLabel =
                                    HEALTH_SCORE_LABEL_BY_BAND[normalizeHealthBand(metricScore)];
                            }
                        });
                    }

                    nextAreaHealthKpis.set(area.id, {
                        areaId: area.id,
                        latestDay: latest ? dayjs(latest.day).toISOString() : null,
                        latestScore: latest ? Number(latest.score.toFixed(2)) : null,
                        latestScoreRounded,
                        latestLabel,
                        latestMetricCount: latest?.metricCount || 0,
                        worstMetricKey,
                        worstMetricName,
                        worstMetricScore,
                        worstMetricLabel,
                        weekTrend: calculateBenchmarkTrend(currentWindowSeries, 7),
                        monthTrend: calculateBenchmarkTrend(currentWindowSeries, 30),
                    });

                    if (latest && Number.isFinite(latest.score)) {
                        currentPeriodAreaScores.push(latest.score);
                    }
                    if (previousLatest && Number.isFinite(previousLatest.score)) {
                        previousPeriodAreaScores.push(previousLatest.score);
                    }
                });

                const nextPortfolioHealthSeries = Array.from(overallCurrentSeriesByDay.entries())
                    .map(([day, scores]) => ({
                        day: dayjs(day).toISOString(),
                        score: Number((scores.reduce((sum, value) => sum + value, 0) / scores.length).toFixed(2)),
                    }))
                    .sort((a, b) => dayjs(a.day).valueOf() - dayjs(b.day).valueOf());

                if (benchmarkDebugEnabled && debugStateByArea) {
                    console.groupCollapsed(
                        `[Dashboard benchmark debug] ${debugStateByArea.size} areas, ${seriesPayload.length} metric series`
                    );
                    areaSummaries.forEach((area) => {
                        const state = debugStateByArea.get(area.id) || createAreaBenchmarkDebugState(area);
                        const kpi = nextAreaHealthKpis.get(area.id) || null;
                        const status = kpi?.latestScoreRounded === null ? 'missing-benchmark' : 'resolved';
                        console.groupCollapsed(
                            `[${status}] ${area.name} (#${area.id}) areaType=${area.areaTypeId ?? 'n/a'}`
                        );
                        console.log('KPI summary', kpi);
                        console.log('Series metric keys', Array.from(state.seriesMetricKeys).sort());
                        console.log('Resolved metrics', Array.from(state.resolvedMetricDetails).sort());
                        if (state.unresolvedMetricDetails.size > 0) {
                            console.warn('Unresolved metrics', Array.from(state.unresolvedMetricDetails).sort());
                        }
                        if (state.missingAveragePoints.size > 0) {
                            console.warn('Invalid averages', Array.from(state.missingAveragePoints).sort());
                        }
                        if (state.noBandMatches.size > 0) {
                            console.warn('No band matches', Array.from(state.noBandMatches).sort());
                        }
                        console.log('Scored days', Array.from(state.scoredDays).sort());
                        console.groupEnd();
                    });
                    console.groupEnd();
                }

                if (benchmarkDebugEnabled) {
                    const missingBenchmarkAreas = areaSummaries.filter(
                        (area) => nextAreaHealthKpis.get(area.id)?.latestScoreRounded === null
                    );

                    const benchmarkExpectations = benchmarkMetrics.map((metric: any) => ({
                        key: String(metric?.key || ''),
                        name: String(metric?.name || metric?.key || ''),
                        sourceKey: String(metric?.sourceKey || ''),
                        valueTypeKey: String(metric?.valueTypeKey || ''),
                    }));

                    const measurementDebugResults = await Promise.allSettled(
                        missingBenchmarkAreas.map(async (area) => {
                            const response = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                                params: {
                                    area_id: area.id,
                                    limit: 5,
                                    offset: 0,
                                },
                                withCredentials: true,
                                signal: controller.signal,
                            });

                            const rows = Array.isArray(response.data?.data) ? response.data.data : [];
                            const measurementSummaries = rows.map((measurement: any) => {
                                const values = Array.isArray(measurement?.values) ? measurement.values : [];
                                return {
                                    id: measurement?.id,
                                    measuredAt: measurement?.measuredAt || measurement?.createdAt || null,
                                    measurementCode: measurement?.measurementCode || null,
                                    valueTypes: values
                                        .map((value: any) => (typeof value?.valueType === 'string' ? value.valueType : ''))
                                        .filter(Boolean),
                                };
                            });

                            const exactValueTypes = new Set<string>(
                                measurementSummaries.flatMap((measurement: any) => measurement.valueTypes)
                            );
                            const normalizedValueTypes = new Set<string>(
                                Array.from(exactValueTypes).map((valueType: string) => valueType.toLowerCase().trim())
                            );

                            const expectedSourceKeys = benchmarkExpectations
                                .map((metric) => metric.sourceKey)
                                .filter(Boolean);
                            const missingExactSourceKeys = expectedSourceKeys.filter(
                                (sourceKey) => !exactValueTypes.has(sourceKey)
                            );
                            const missingNormalizedSourceKeys = expectedSourceKeys.filter(
                                (sourceKey) => !normalizedValueTypes.has(sourceKey.toLowerCase().trim())
                            );

                            return {
                                area,
                                measurementSummaries,
                                expectedMetrics: benchmarkExpectations,
                                exactValueTypes: Array.from(exactValueTypes).sort(),
                                normalizedValueTypes: Array.from(normalizedValueTypes).sort(),
                                missingExactSourceKeys,
                                missingNormalizedSourceKeys,
                            };
                        })
                    );

                    measurementDebugResults.forEach((result) => {
                        if (result.status === 'rejected') {
                            console.warn('[Dashboard benchmark debug] measurement valueType inspection failed', result.reason);
                            return;
                        }

                        const {
                            area,
                            measurementSummaries,
                            expectedMetrics,
                            exactValueTypes,
                            normalizedValueTypes,
                            missingExactSourceKeys,
                            missingNormalizedSourceKeys,
                        } = result.value;

                        console.groupCollapsed(
                            `[Dashboard benchmark debug] measurement valueTypes for ${area.name} (#${area.id})`
                        );
                        console.log('Expected benchmark metrics', expectedMetrics);
                        console.log('Recent measurements', measurementSummaries);
                        console.log('Exact measurement valueTypes', exactValueTypes);
                        console.log('Normalized measurement valueTypes', normalizedValueTypes);
                        console.warn('Missing exact sourceKeys', missingExactSourceKeys);
                        console.warn('Missing normalized sourceKeys', missingNormalizedSourceKeys);
                        console.groupEnd();
                    });
                }

                setAreaHealthKpis(nextAreaHealthKpis);
                setPortfolioHealthSeries(nextPortfolioHealthSeries);
                const currentOverall =
                    currentPeriodAreaScores.length > 0
                        ? currentPeriodAreaScores.reduce((sum, score) => sum + score, 0) /
                          currentPeriodAreaScores.length
                        : null;
                const previousOverall =
                    previousPeriodAreaScores.length > 0
                        ? previousPeriodAreaScores.reduce((sum, score) => sum + score, 0) /
                          previousPeriodAreaScores.length
                        : null;

                let overallDirection: TrendDirection = 'none';
                let overallDelta: number | null = null;
                if (currentOverall !== null && previousOverall !== null) {
                    const delta = currentOverall - previousOverall;
                    overallDelta = Number(delta.toFixed(2));
                    if (delta > 0.1) overallDirection = 'up';
                    else if (delta < -0.1) overallDirection = 'down';
                    else overallDirection = 'same';
                }

                setOverallSurfaceHealthTrend({
                    direction: overallDirection,
                    delta: overallDelta,
                    currentScore: currentOverall !== null ? Number(currentOverall.toFixed(2)) : null,
                    previousScore: previousOverall !== null ? Number(previousOverall.toFixed(2)) : null,
                });
            } catch (error: any) {
                if (axios.isCancel(error) || error?.name === 'CanceledError') {
                    return;
                }
                console.warn('Dashboard area health KPI fetch failed', error);
                setAreaHealthKpis(new Map());
                setOverallSurfaceHealthTrend(null);
                setPortfolioHealthSeries([]);
            } finally {
                setLoadingAreaHealthKpis(false);
            }

            return () => {
                controller.abort();
            };
        };

        let cleanup: (() => void) | void;
        void (async () => {
            cleanup = await loadAreaHealthKpis();
        })();

        return () => {
            if (cleanup) cleanup();
        };
    }, [companyId, areaSummaries, benchmarkMetrics, benchmarkMetricCandidateIndex, riskTrendWindow]);

    const healthAreaIds = useMemo(() => areaSummaries.map((area) => area.id), [areaSummaries]);
    const healthStatusState = useAreaHealthStatuses(healthAreaIds, {
        enabled: healthAreaIds.length > 0,
    });

    const healthStatusDetailsByAreaId = useMemo(() => {
        return healthStatusState.statuses.reduce((acc, summary) => {
            acc.set(summary.areaId, summary);
            return acc;
        }, new Map<number, AreaHealthStatusRow>());
    }, [healthStatusState.statuses]);

    const metricDefinitionByKey = useMemo(() => {
        const map = new Map<string, MetricDefinition>();
        metricDefinitions.forEach((metric) => {
            if (metric.key) {
                map.set(metric.key.toLowerCase(), metric);
            }
        });
        return map;
    }, [metricDefinitions]);

    const handleMeasurementNavigate = useCallback(
        (measurementId: string | number) => {
            navigate(`/measurements/${measurementId}`);
        },
        [navigate]
    );

    const handleAreaNavigate = useCallback(
        (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => {
            const dashboardNav: { areaId: number; dateFrom?: string; dateTo?: string } = { areaId };
            if (rangeOverride) {
                dashboardNav.dateFrom = rangeOverride[0].toISOString();
                dashboardNav.dateTo = rangeOverride[1].toISOString();
            }
            navigate('/reports', {
                state: { dashboardNav },
            });
        },
        [navigate]
    );

    const handleWarningNavigate = useCallback(
        (warning: DashboardWarning) => {
            const warningDate = dayjs(warning.dateFrom || warning.dateTo || undefined);
            const range =
                warningDate.isValid()
                    ? {
                          dateFrom: warningDate.startOf('day').toISOString(),
                          dateTo: warningDate.endOf('day').toISOString(),
                      }
                    : {};

            navigate('/reports', {
                state: {
                    dashboardNav: {
                        areaId: warning.areaId,
                        ...range,
                    },
                },
            });
        },
        [navigate]
    );

    const handleHideWarning = useCallback(async (warningId: number) => {
        try {
            await axios.post(ENDPOINTS.NOTIFICATION_HIDE(warningId), {}, { withCredentials: true });
            setInsights((prev) =>
                prev
                    ? {
                          ...prev,
                          warnings: prev.warnings.filter((warning) => warning.id !== warningId),
                      }
                    : prev
            );
        } catch (error) {
            console.warn('Failed to hide notification', error);
            message.error('Unable to hide this warning.');
        }
    }, []);

    const handleDeleteWarning = useCallback(async (warningId: number) => {
        try {
            await axios.delete(ENDPOINTS.NOTIFICATION_DELETE(warningId), { withCredentials: true });
            setInsights((prev) =>
                prev
                    ? {
                          ...prev,
                          warnings: prev.warnings.filter((warning) => warning.id !== warningId),
                      }
                    : prev
            );
        } catch (error) {
            console.warn('Failed to delete notification', error);
            message.error('Unable to delete this warning.');
        }
    }, []);

    const last7DaysRange = useMemo(() => {
        const end = dayjs().endOf('day');
        const start = end.subtract(6, 'day').startOf('day');
        return [start, end] as [Dayjs, Dayjs];
    }, []);

    const selectedRiskTrendRange = useMemo<[Dayjs, Dayjs]>(() => {
        return getRiskTrendDateRange(riskTrendWindow);
    }, [riskTrendWindow]);

    const favoriteMeasurementRange = useMemo(() => {
        if (!favoriteMeasurement?.takenAt) return null;
        const range = measurementLocalDayUtcRange(favoriteMeasurement.takenAt, favoriteMeasurement);
        return range ? [dayjs(range[0]), dayjs(range[1])] as [Dayjs, Dayjs] : null;
    }, [favoriteMeasurement]);

    const buildMetricsToShow = useCallback(
        (measurement: LatestMeasurement | null): DashboardMetricDisplay[] => {
            if (!measurement?.metrics) return [];

            const entries = Object.entries(measurement.metrics);
            const entryByKey = new Map(entries.map(([key, value]) => [key, value]));
            const availableKeyMap = new Map(entries.map(([key]) => [key.toLowerCase(), key]));

            const findDefinitionForSelection = (selectedKey: string) => {
                const selectedLower = selectedKey.toLowerCase();
                const direct = metricDefinitionByKey.get(selectedLower);
                if (direct) return direct;

                for (const def of metricDefinitions) {
                    const aliases = [def.key, def.valueTypeKey, def.sourceKey].filter(Boolean) as string[];
                    if (aliases.some((alias) => alias.toLowerCase() === selectedLower)) return def;
                }
                return undefined;
            };

            const resolveMetricKey = (selectedKey: string, definition?: MetricDefinition) => {
                const candidates = new Set<string>();
                if (definition?.valueTypeKey) candidates.add(definition.valueTypeKey);
                if (definition?.sourceKey) candidates.add(definition.sourceKey);
                if (definition?.key) candidates.add(definition.key);
                candidates.add(selectedKey);

                for (const candidate of candidates) {
                    const resolved = availableKeyMap.get(candidate.toLowerCase());
                    if (resolved) return resolved;
                }
                return null;
            };

            const formatEntry = (
                metricKey: string,
                label: string,
                unit: string | null | undefined,
                value: number | null | undefined,
                trend: TrendDirection,
                positiveIsGood: boolean
            ) => {
                const displayValue = typeof value === 'number' ? value.toFixed(2) : '-';
                return {
                    key: metricKey,
                    label,
                    value: unit && displayValue !== '-' ? `${displayValue} ${unit}` : displayValue,
                    trend,
                    positiveIsGood,
                };
            };

            if (preferredMetricKeys.length === 0) {
                return entries.map(([key]) => {
                    const def = metricDefinitionByKey.get(key.toLowerCase());
                    const label = def?.name || key.replace(/_/g, ' ');
                    const unit = def?.unit;
                    const value = entryByKey.get(key) ?? null;
                    const trend = measurement.trends?.[key.toLowerCase()]?.direction || 'none';
                    const positiveIsGood = def?.positiveIsGood ?? true;
                    return formatEntry(key, label, unit, value, trend, positiveIsGood);
                });
            }

            return preferredMetricKeys.map((selectedKey) => {
                const def = findDefinitionForSelection(selectedKey);
                const resolvedKey = resolveMetricKey(selectedKey, def);
                const lookupKey = resolvedKey || selectedKey;
                const value = resolvedKey ? entryByKey.get(resolvedKey) ?? null : undefined;
                const trend = resolvedKey
                    ? measurement.trends?.[resolvedKey.toLowerCase()]?.direction || 'none'
                    : 'none';
                const label = def?.name || selectedKey.replace(/_/g, ' ');
                const unit = def?.unit;
                const positiveIsGood = def?.positiveIsGood ?? true;
                return formatEntry(lookupKey, label, unit, value, trend, positiveIsGood);
            });
        },
        [metricDefinitionByKey, metricDefinitions, preferredMetricKeys]
    );

    const warnings = useMemo(() => insights?.warnings || [], [insights]);

    const warningsToShow = useMemo(() => {
        if (showAllWarnings) return warnings;
        return warnings.slice(0, 1);
    }, [showAllWarnings, warnings]);

    const warningCountByAreaId = useMemo(() => {
        const [windowStart, windowEnd] = selectedRiskTrendRange;

        const isWarningInSelectedWindow = (warning: DashboardWarning) => {
            const rawFrom = warning.dateFrom || warning.dateTo || null;
            const rawTo = warning.dateTo || warning.dateFrom || null;
            if (!rawFrom || !rawTo) return true;

            const parsedFrom = dayjs(rawFrom);
            const parsedTo = dayjs(rawTo);
            if (!parsedFrom.isValid() || !parsedTo.isValid()) return true;

            const rangeStart = parsedFrom.isBefore(parsedTo) ? parsedFrom : parsedTo;
            const rangeEnd = parsedFrom.isBefore(parsedTo) ? parsedTo : parsedFrom;

            return !(rangeEnd.isBefore(windowStart) || rangeStart.isAfter(windowEnd));
        };

        return warnings.filter(isWarningInSelectedWindow).reduce((map, warning) => {
            map.set(warning.areaId, (map.get(warning.areaId) || 0) + 1);
            return map;
        }, new Map<number, number>());
    }, [warnings, selectedRiskTrendRange]);

    const healthCounts = useMemo<Record<HealthStatus, number>>(() => {
        const counts: Record<HealthStatus, number> = {
            healthy: 0,
            attention: 0,
            'action-needed': 0,
            'no-data': 0,
        };

        areaSummaries.forEach((area) => {
            const roundedKpi = areaHealthKpis.get(area.id)?.latestScoreRounded ?? null;
            if (roundedKpi === null) {
                counts['no-data'] += 1;
                return;
            }

            if (roundedKpi >= 4) {
                counts.healthy += 1; // Great, Perfect
                return;
            }
            if (roundedKpi >= 2) {
                counts.attention += 1; // Good, Fair
                return;
            }
            counts['action-needed'] += 1; // Poor
        });

        return counts;
    }, [areaSummaries, areaHealthKpis]);

    const overallSurfaceHealthScore = useMemo(() => {
        const kpiScores = areaSummaries
            .map((area) => areaHealthKpis.get(area.id)?.latestScore ?? null)
            .filter((score): score is number => score !== null && Number.isFinite(score));

        if (kpiScores.length === 0) return null;

        const avgKpi = kpiScores.reduce((sum, score) => sum + score, 0) / kpiScores.length;
        return Number(avgKpi.toFixed(2));
    }, [areaSummaries, areaHealthKpis]);

    const riskRows = useMemo<ExecutiveRiskRow[]>(() => {
        const rowByAreaId = new Map<number, ExecutiveRiskRow>();
        const childIdsByParentId = new Map<number, number[]>();
        const [windowStart, windowEnd] = selectedRiskTrendRange;

        const isLatestScanInSelectedWindow = (latestAt: string | null) => {
            if (!latestAt) return false;
            const parsed = dayjs(latestAt);
            if (!parsed.isValid()) return false;
            return !parsed.isBefore(windowStart) && !parsed.isAfter(windowEnd);
        };

        areaSummaries.forEach((area) => {
            const statusDetail = healthStatusDetailsByAreaId.get(area.id);
            const status = statusDetail?.status || 'no-data';
            const warningCount = warningCountByAreaId.get(area.id) || 0;
            const healthKpi = areaHealthKpis.get(area.id);
            const selectedTrend = riskTrendWindow === 'week' ? healthKpi?.weekTrend : healthKpi?.monthTrend;
            const latestHealthScoreRounded = healthKpi?.latestScoreRounded ?? null;
            const latestHealthScore = healthKpi?.latestScore ?? null;
            const latestHealthLabel = healthKpi?.latestLabel ?? null;
            const worstMetricKey = healthKpi?.worstMetricKey ?? null;
            const worstMetricName = healthKpi?.worstMetricName ?? null;
            const worstMetricScore = healthKpi?.worstMetricScore ?? null;
            const worstMetricLabel = healthKpi?.worstMetricLabel ?? null;
            const latestAt = statusDetail?.latestAt || null;
            const canUseFallback = isLatestScanInSelectedWindow(latestAt);
            const riskLevel =
                latestHealthScoreRounded !== null || canUseFallback
                    ? resolveRiskLevel(latestHealthScoreRounded, status, warningCount)
                    : 'unknown';

            const trend: TrendDirection = selectedTrend?.direction || 'none';
            const trendDelta = selectedTrend?.delta ?? null;

            rowByAreaId.set(area.id, {
                key: area.id,
                areaId: area.id,
                parentAreaId: area.parentAreaId ?? null,
                areaName: area.name,
                status,
                trend,
                trendDelta,
                riskLevel,
                warningCount,
                percentOutOfRange: statusDetail?.percentOutOfRange || 0,
                outOfRangeCount: statusDetail?.outOfRangeCount || 0,
                totalCount: statusDetail?.totalCount || 0,
                latestAt,
                latestHealthScore,
                latestHealthScoreRounded,
                latestHealthLabel,
                worstMetricKey,
                worstMetricName,
                worstMetricScore,
                worstMetricLabel,
            });

            if (area.parentAreaId && Number.isFinite(Number(area.parentAreaId))) {
                const parentId = Number(area.parentAreaId);
                const list = childIdsByParentId.get(parentId) || [];
                list.push(area.id);
                childIdsByParentId.set(parentId, list);
            }
        });

        const buildRow = (areaId: number): ExecutiveRiskRow | null => {
            const base = rowByAreaId.get(areaId);
            if (!base) return null;

            const childIds = childIdsByParentId.get(areaId) || [];
            const children = childIds
                .map((childId) => buildRow(childId))
                .filter((row): row is ExecutiveRiskRow => row !== null)
                .sort(compareRiskRows);

            if (children.length === 0) {
                return base;
            }

            const childScores = children
                .map((child) => child.latestHealthScore)
                .filter((score): score is number => score !== null && Number.isFinite(score));
            const childTrendDeltas = children
                .map((child) => child.trendDelta)
                .filter((delta): delta is number => delta !== null && Number.isFinite(delta));
            const childWarningCount = children.reduce((sum, child) => sum + (child.warningCount || 0), 0);

            const aggregatedScore =
                childScores.length > 0
                    ? Number((childScores.reduce((sum, value) => sum + value, 0) / childScores.length).toFixed(2))
                    : null;
            const aggregatedRounded = aggregatedScore !== null ? normalizeHealthBand(aggregatedScore) : null;
            const aggregatedLabel =
                aggregatedRounded !== null ? HEALTH_SCORE_LABEL_BY_BAND[aggregatedRounded] : null;

            const aggregatedTrendDelta =
                childTrendDeltas.length > 0
                    ? Number((childTrendDeltas.reduce((sum, value) => sum + value, 0) / childTrendDeltas.length).toFixed(2))
                    : null;
            const latestChildAt =
                children
                    .map((child) => child.latestAt)
                    .filter((value): value is string => Boolean(value) && dayjs(value).isValid())
                    .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || null;
            const latestAt = latestChildAt || base.latestAt || null;

            const worstChildMetric = children
                .filter((child) => child.worstMetricScore !== null)
                .sort((a, b) => {
                    const scoreA = a.worstMetricScore as number;
                    const scoreB = b.worstMetricScore as number;
                    if (Math.abs(scoreA - scoreB) > 0.0001) return scoreA - scoreB;
                    return (a.worstMetricName || '').localeCompare(b.worstMetricName || '');
                })[0];

            let aggregatedTrend: TrendDirection = 'none';
            if (aggregatedTrendDelta !== null) {
                if (aggregatedTrendDelta > 0.1) aggregatedTrend = 'up';
                else if (aggregatedTrendDelta < -0.1) aggregatedTrend = 'down';
                else aggregatedTrend = 'same';
            }

            return {
                ...base,
                trend: aggregatedTrend,
                trendDelta: aggregatedTrendDelta,
                warningCount: childWarningCount,
                latestHealthScore: aggregatedScore,
                latestHealthScoreRounded: aggregatedRounded,
                latestHealthLabel: aggregatedLabel,
                latestAt,
                worstMetricKey: worstChildMetric?.worstMetricKey || null,
                worstMetricName: worstChildMetric?.worstMetricName || null,
                worstMetricScore: worstChildMetric?.worstMetricScore || null,
                worstMetricLabel: worstChildMetric?.worstMetricLabel || null,
                riskLevel:
                    aggregatedRounded !== null || isLatestScanInSelectedWindow(latestAt)
                        ? resolveRiskLevel(aggregatedRounded, base.status, childWarningCount)
                        : 'unknown',
                children,
            };
        };

        const rootIds = areaSummaries
            .filter((area) => {
                const parentId = area.parentAreaId ?? null;
                return !parentId || !rowByAreaId.has(parentId);
            })
            .map((area) => area.id);

        return rootIds
            .map((rootId) => buildRow(rootId))
            .filter((row): row is ExecutiveRiskRow => row !== null)
            .sort(compareRiskRows);
    }, [
        areaSummaries,
        areaHealthKpis,
        healthStatusDetailsByAreaId,
        selectedRiskTrendRange,
        riskTrendWindow,
        warningCountByAreaId,
    ]);

    const flattenedRiskRows = useMemo(() => {
        return flattenRiskRows(riskRows).sort(compareRiskRows);
    }, [riskRows]);

    const topCriticalAreas = useMemo(() => {
        return flattenedRiskRows.filter((row) => row.riskLevel === 'critical').slice(0, 3);
    }, [flattenedRiskRows]);

    const topRiskArea = useMemo(() => {
        return flattenedRiskRows[0] || null;
    }, [flattenedRiskRows]);

    const favoriteAreaMetrics = useMemo(
        () => buildMetricsToShow(favoriteMeasurement),
        [buildMetricsToShow, favoriteMeasurement]
    );

    const mostVisitedCollapseItems = useMemo<DashboardCollapseItem[]>(() => {
        const list = insights?.mostVisited || [];
        const summaries = insights?.mostVisitedSummaries || [];

        const measurementByAreaId = new Map<number, LatestMeasurement | null>();
        const dayMeasurementsByAreaId = new Map<
            number,
            Array<{ id: number; lat: number | null; lng: number | null; measurementCode: string | null }>
        >();

        summaries.forEach((summary) => {
            measurementByAreaId.set(summary.areaId, summary.measurement ?? null);
            dayMeasurementsByAreaId.set(summary.areaId, summary.dayMeasurements || []);
        });

        const areasWithMeasurements = list.filter((item) => {
            return measurementByAreaId.has(item.areaId) && measurementByAreaId.get(item.areaId) !== null;
        });

        return areasWithMeasurements.map((item) => {
            const measurement = measurementByAreaId.get(item.areaId) ?? null;
            const dayMeasurements = dayMeasurementsByAreaId.get(item.areaId) || [];
            const metricsToShow = buildMetricsToShow(measurement);
            const measurementRange = measurement?.takenAt
                ? (() => {
                      const range = measurementLocalDayUtcRange(measurement.takenAt, measurement);
                      return range ? ([dayjs(range[0]), dayjs(range[1])] as [Dayjs, Dayjs]) : null;
                  })()
                : null;

            const headerMetrics = metricsToShow.slice(0, 4);

            return {
                key: String(item.areaId),
                label: (
                    <div className="dashboard-area-row">
                        <div className="dashboard-area-title">
                            <Space size="middle" wrap>
                                <Text strong>{item.areaName}</Text>
                                <Text type="secondary">Visits: {item.visits}</Text>
                            </Space>
                        </div>

                        {headerMetrics.length > 0 ? (
                            <Space className="dashboard-metric-row" size={[8, 8]} wrap={false}>
                                {headerMetrics.map((metric) => {
                                    const getVariantClass = () => {
                                        if (metric.trend === 'same' || metric.trend === 'none') {
                                            return 'neutral';
                                        }
                                        const positiveDirection = Boolean(metric.positiveIsGood);
                                        const isUp = metric.trend === 'up';
                                        return isUp === positiveDirection ? 'positive' : 'negative';
                                    };
                                    const variant = getVariantClass();
                                    return (
                                        <Tag
                                            key={metric.key}
                                            className={`dashboard-metric-tag dashboard-metric-tag-${variant}`}
                                            style={{ marginInlineEnd: 0 }}
                                        >
                                            <Space size={6}>
                                                <TrendIndicator
                                                    trend={metric.trend as TrendDirection}
                                                    positiveIsGood={metric.positiveIsGood}
                                                    size="large"
                                                />
                                                <span style={{ fontWeight: 600 }}>{metric.label}</span>
                                                <span>{metric.value}</span>
                                            </Space>
                                        </Tag>
                                    );
                                })}
                                {metricsToShow.length > headerMetrics.length && (
                                    <Tag className="dashboard-metric-tag dashboard-metric-tag-neutral">
                                        +{metricsToShow.length - headerMetrics.length}
                                    </Tag>
                                )}
                            </Space>
                        ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                No metrics available
                            </Text>
                        )}
                    </div>
                ),
                children: (
                    <div style={{ paddingTop: 8 }}>
                        <MeasurementSummaryCard
                            title={null}
                            measurement={measurement}
                            templateImageUrl={templateImageUrl}
                            metricsToShow={metricsToShow}
                            onMeasurementClick={handleMeasurementNavigate}
                            reportDateRange={measurementRange}
                            areaId={item.areaId}
                            dayMeasurements={dayMeasurements}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Button type="primary" onClick={() => handleAreaNavigate(item.areaId, last7DaysRange)}>
                                View report
                            </Button>
                        </div>
                    </div>
                ),
            };
        });
    }, [
        insights?.mostVisited,
        insights?.mostVisitedSummaries,
        buildMetricsToShow,
        handleAreaNavigate,
        handleMeasurementNavigate,
        templateImageUrl,
        last7DaysRange,
    ]);

    const handleAreaNavigateForRiskWindow = useCallback(
        (areaId: number) => {
            handleAreaNavigate(areaId, selectedRiskTrendRange);
        },
        [handleAreaNavigate, selectedRiskTrendRange]
    );

    const handleOpenExecutiveReport = useCallback(() => {
        if (topRiskArea?.areaId) {
            handleAreaNavigate(topRiskArea.areaId, selectedRiskTrendRange);
            return;
        }
        navigate('/reports');
    }, [topRiskArea, handleAreaNavigate, selectedRiskTrendRange, navigate]);

    const executiveLoading =
        loadingAreas || healthStatusState.loading || loadingBenchmarkMetrics || loadingAreaHealthKpis;

    if (!companyId) {
        return (
            <div className="dashboard-page">
                <Empty description="Select a company to load the dashboard." />
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <div className="dashboard-header-top">
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            Dashboard
                        </Title>
                        <Text type="secondary">
                            {dashboardMode === 'executive'
                                ? 'Priority-first executive layout for faster triage.'
                                : 'Operational view for field analysis and actions.'}
                        </Text>

                        {favoriteArea && (
                            <Space size="small" style={{ marginTop: 4 }} className="dashboard-favorite-inline">
                                <Text type="secondary">Favorite area:</Text>
                                <Text strong>{favoriteArea.name}</Text>
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => handleAreaNavigate(favoriteArea.id, last7DaysRange)}
                                >
                                    View
                                </Button>
                            </Space>
                        )}
                    </div>

                    <div className="dashboard-header-controls">
                        {dashboardMode !== 'operational' ? (
                            <Space className="dashboard-window-switch" size="small">
                                <Text type="secondary">Window</Text>
                                <Segmented
                                    options={[
                                        { label: 'Week', value: 'week' },
                                        { label: 'Month', value: 'month' },
                                    ]}
                                    value={riskTrendWindow}
                                    onChange={(value) => setRiskTrendWindow(value as RiskTrendWindow)}
                                />
                            </Space>
                        ) : null}

                        <Space className="dashboard-mode-switch" size="small">
                            <Text type="secondary">View</Text>
                            <Segmented
                                options={[
                                    { label: 'Executive', value: 'executive' },
                                    { label: 'Operational', value: 'operational' },
                                ]}
                                value={dashboardMode}
                                onChange={(value) => setDashboardMode(value as DashboardMode)}
                            />
                        </Space>
                    </div>
                </div>
            </div>

            {dashboardMode === 'executive' ? (
                <DashboardExecutiveView
                    overallSurfaceHealthScore={overallSurfaceHealthScore}
                    overallSurfaceHealthTrend={overallSurfaceHealthTrend}
                    portfolioHealthSeries={portfolioHealthSeries}
                    healthCounts={healthCounts}
                    scanTrendSnapshot={scanTrendSnapshot}
                    loadingScanTrend={loadingScanTrend}
                    executiveLoading={executiveLoading}
                    areaCount={areaSummaries.length}
                    riskRows={riskRows}
                    onAreaNavigate={handleAreaNavigateForRiskWindow}
                    onOpenExecutiveReport={handleOpenExecutiveReport}
                    riskTrendWindow={riskTrendWindow}
                />
            ) : (
                <DashboardOperationalView
                    warnings={warnings}
                    warningsToShow={warningsToShow}
                    showAllWarnings={showAllWarnings}
                    onToggleShowAllWarnings={() => setShowAllWarnings((prev) => !prev)}
                    onHideWarning={handleHideWarning}
                    onDeleteWarning={handleDeleteWarning}
                    onWarningNavigate={handleWarningNavigate}
                    companyId={companyId}
                    preferredMetricKeys={preferredMetricKeys}
                    onMeasurementNavigate={handleMeasurementNavigate}
                    onAreaNavigate={handleAreaNavigate}
                    favoriteMeasurement={favoriteMeasurement}
                    loadingFavoriteMeasurement={loadingFavoriteMeasurement}
                    favoriteAreaMetrics={favoriteAreaMetrics}
                    favoriteMeasurementRange={favoriteMeasurementRange}
                    templateImageUrl={templateImageUrl}
                    loadingInsights={loadingInsights}
                    hasMostVisited={(insights?.mostVisited?.length || 0) > 0}
                    mostVisitedCollapseItems={mostVisitedCollapseItems}
                />
            )}
        </div>
    );
};

export default Dashboard;
